import { isDefined } from "@tournated-com/ui";

import {
  DashboardCreateUserAdditionalRolesItem,
  DashboardCreateUserGender,
  DashboardCurrentUser,
  DashboardUpdateUserAdditionalRolesItem,
  DashboardUpdateUserGender,
  DashboardUserDetail,
  DashboardUserListItem,
  SignupSource,
  DashboardUsersControllerListGender as ApiGender,
} from "@/api/__generated__/schemas";
import {
  ApiUser,
  BaseDetailedUserFragment,
  BaseUserFragment,
  DetailedUserWithFederationsFragment,
  DetailedUserWithPlatformsFragment,
  DetailedUserWithSportsFragment,
  fromApiCalendarDateNullable,
  fromApiDate,
  Signup_Source,
  ThirdPartyUserProfileConnectionType,
  UserWithMemberOfPlatformsFragment,
  UserWithSportsFragment,
} from "@/graphql";
import { mapToBaseClub, mapToClubReferenceFromRef, mapToOrganization } from "@/mappers";
import { mapToCapabilitySet } from "@/mappers/capability.mappers";
import {
  mapAvatarFields,
  mapToCountryCodeNullable,
  mapToCurrencyCode,
} from "@/mappers/common.mappers";
import { mapToBasePlatform } from "@/mappers/platform.mappers";
import { mapToScopePair } from "@/mappers/scope.mappers";
import { mapToSport, mapToSportRef } from "@/mappers/sport.mappers";
import {
  User,
  CurrentUser,
  DetailedUser,
  UserPlatformRole,
  UserRole,
  UserSignupSource,
  UserWithPlatforms,
  UserWithSports,
  UserWithMemberOfPlatforms,
  OrganizerUser,
  Gender,
  ThirdPartyLicenseType,
  AdditionalUserRole,
  UserReference,
  UserListItem,
  UserConnections,
  UserDetail,
  UserWithOrganizations,
  isUSState,
} from "@/types";

export const mapToUser = (fragment: BaseUserFragment): User => ({
  id: String(fragment.id ?? 0),
  avatar: fragment.avatar ?? null,
  email: fragment.email!,
  name: fragment.name!,
  surname: fragment.surname!,
  instagram: fragment.instagram ?? null,
  phone: fragment.phone ?? null,
  role: fragment.role ? mapToUserRole(fragment.role) : null,
  nation: fragment.nation ? mapToCountryCodeNullable(fragment.nation) : null,
  gender: fragment.gender ? mapToGender(fragment.gender) : null,
  city: fragment.city ?? "",
  country: fragment.country ? mapToCountryCodeNullable(fragment.country) : null,
  isPhoneVerified: fragment.mobileVerified ?? false,
  // TODO: to be returned, when backend is ready
  birthDate: fragment.dob ? fromApiCalendarDateNullable(fragment.dob) : null,
  emailConfirmed: false,
  // emailConfirmed: mapToEmailConfirmed(fragment.email_confirmed),
  uciId: fragment.uciId ?? null,
  additionalRoles: fragment.additionalUserRole
    ? mapToAdditionalUserRoles(fragment.additionalUserRole)
    : [],
  postalCode: fragment.postalCode ?? null,
  wprPlayerId: fragment.wprPlayerId ?? null,
  wprUserId: fragment.wprUserId ?? null,
});

type OrganizerUserFragment = Omit<BaseUserFragment, "__typename"> & {
  __typename?: string;
  middle_name?: string | null;
  name_armenian?: string | null;
  surname_armenian?: string | null;
  representingCountry?: string | null;
  locationCountry?: string | null;
  stripeAccountId?: string | null;
  stripeConnectOnboardingComplete?: boolean | null;
};

export const mapToOrganizerUser = (fragment: OrganizerUserFragment): OrganizerUser => ({
  id: String(fragment.id ?? 0),
  avatar: fragment.avatar ?? null,
  avatarFile: null,
  legacyFileUrl: fragment.avatar ?? null,
  email: fragment.email ?? "",
  name: fragment.name ?? "",
  surname: fragment.surname ?? "",
  instagram: fragment.instagram ?? null,
  phone: fragment.phone ?? null,
  role: fragment.role ? mapToUserRole(fragment.role) : null,
  nation: fragment.nation ? mapToCountryCodeNullable(fragment.nation) : null,
  gender: fragment.gender ? mapToGender(fragment.gender) : null,
  city: fragment.city ?? "",
  country: fragment.country ? mapToCountryCodeNullable(fragment.country) : null,
  isPhoneVerified: fragment.mobileVerified ?? false,
  birthDate: fragment.dob ? fromApiCalendarDateNullable(fragment.dob) : null,
  emailConfirmed: false,
  uciId: fragment.uciId ?? null,
  additionalRoles: fragment.additionalUserRole
    ? mapToAdditionalUserRoles(fragment.additionalUserRole)
    : [],
  postalCode: fragment.postalCode ?? null,
  wprPlayerId: fragment.wprPlayerId ?? null,
  wprUserId: fragment.wprUserId ?? null,
  middleName: fragment.middle_name ?? null,
  nameArmenian: fragment.name_armenian ?? null,
  surnameArmenian: fragment.surname_armenian ?? null,
  representingCountry: fragment.representingCountry
    ? mapToCountryCodeNullable(fragment.representingCountry)
    : null,
  locationCountry: fragment.locationCountry ?? null,
  stripeAccountId: fragment.stripeAccountId ?? null,
  stripeConnectOnboardingComplete: fragment.stripeConnectOnboardingComplete ?? null,
});

export const mapDetailedUserToUser = (fragment: BaseDetailedUserFragment): User => ({
  id: String(fragment.id ?? 0),
  avatar: fragment.avatar!,
  email: fragment.email!,
  name: fragment.name!,
  surname: fragment.surname!,
  instagram: fragment.instagram ?? null,
  phone: fragment.phone ?? null,
  role: fragment.role ? mapToUserRole(fragment.role) : null,
  nation: fragment.nation ? mapToCountryCodeNullable(fragment.nation) : null,
  gender: fragment.gender ? mapToGender(fragment.gender) : null,
  city: fragment.city ?? "",
  country: fragment.country ? mapToCountryCodeNullable(fragment.country) : null,
  isPhoneVerified: fragment.mobileVerified ?? false,
  emailConfirmed: fragment.email_confirmed ? mapToEmailConfirmed(fragment.email_confirmed) : false,
  birthDate: fragment.dob ? fromApiCalendarDateNullable(fragment.dob) : null,
  uciId: fragment.uciId ?? null,
  additionalRoles: fragment.additionalUserRole
    ? mapToAdditionalUserRoles(fragment.additionalUserRole)
    : [],
  postalCode: fragment.postalCode ?? null,
  wprPlayerId: fragment.wprPlayerId ?? null,
  wprUserId: fragment.wprUserId ?? null,
});

const mapToEmailConfirmed = (emailConfirmed: string): boolean => {
  switch (emailConfirmed.toLowerCase()) {
    case "y":
    case "confirmed":
    case "true":
      return true;
    default:
      return false;
  }
};

export const mapApiUserToBaseUser = (apiUser: ApiUser): User => ({
  id: String(apiUser.id),
  avatar: apiUser.avatar,
  email: apiUser.email,
  name: apiUser.name,
  surname: apiUser.surname,
  instagram: apiUser.instagram ?? null,
  phone: apiUser.phone ?? null,
  role: apiUser.role ? mapToUserRole(apiUser.role) : null,
  gender: apiUser.gender ? mapToGender(apiUser.gender) : null,
  nation: apiUser.nation ? mapToCountryCodeNullable(apiUser.nation) : null,
  city: "",
  country: null,
  isPhoneVerified: apiUser.mobileVerified ?? false,
  emailConfirmed: false,
  birthDate: null,
  uciId: null,
  additionalRoles: [],
  postalCode: null,
  wprPlayerId: null,
  wprUserId: null,
});

export const mapToDetailedUser = (fragment: BaseDetailedUserFragment): DetailedUser => {
  const educationStartDate = fragment.educationStartDate
    ? fromApiCalendarDateNullable(fragment.educationStartDate)
    : null;
  const educationEndDate = fragment.educationEndDate
    ? fromApiCalendarDateNullable(fragment.educationEndDate)
    : null;

  const educationPeriod =
    educationStartDate && educationEndDate
      ? {
          start: educationStartDate,
          end: educationEndDate,
        }
      : null;

  return {
    ...mapDetailedUserToUser(fragment),
    // GraphQL exposes only the flat legacy `avatar` URL — surface it as the
    // legacy avatar field so `resolveAvatarUrl` still resolves it at render.
    avatarFile: null,
    legacyFileUrl: fragment.avatar ?? null,
    middleName: fragment.middle_name ?? null,
    city: fragment.city ?? null,
    street: fragment.street ?? null,
    clubText: fragment.user_club_text ?? null,
    nation: fragment.nation ? mapToCountryCodeNullable(fragment.nation) : null,
    birthDate: fragment.dob ? fromApiCalendarDateNullable(fragment.dob) : null,
    country: fragment.country ? mapToCountryCodeNullable(fragment.country) : null,
    citizenship: fragment.citizenship ? mapToCountryCodeNullable(fragment.citizenship) : null,
    ipinId: fragment.ipinId ?? null,
    duprId: fragment.duprId ?? null,
    teId: fragment.teId ?? null,
    ltsU10Id: fragment.ltsU10Id ?? null,
    atpId: fragment.atpId ?? null,
    wtaId: fragment.wtaId ?? null,
    fideId: fragment.fideId ?? null,
    teU12Id: fragment.teU12Id ?? null,
    bonusU14: fragment.bonusU14 ?? null,
    bonusU12: fragment.bonusU12 ?? null,
    bonusPoints: fragment.bonusPoints ?? null,
    itfPoints: fragment.itf_points ?? null,
    // `fipPoints` is present in the updated backend schema. Keep this narrow
    // compatibility cast until GraphQL codegen runs against that deployed schema.
    fipPoints:
      (fragment as BaseDetailedUserFragment & { fipPoints?: string | null }).fipPoints ?? null,
    fivb: fragment.fivb ?? null,
    bonusU16: fragment.bonusU16 ?? null,
    otherPoints: fragment.otherPoints ?? null,
    wprUserId: fragment.wprUserId ?? null,
    createdAt: fromApiDate(fragment.createdAt),
    wprPlayerId: fragment.wprPlayerId ?? null,
    entries: fragment.entries ?? null,
    paidAmount: fragment.paidAmount ?? null,
    signupSource: fragment.signupSource
      ? mapToUserSignupSourceFromGraphQL(fragment.signupSource)
      : null,
    lastResetPassword: fragment.lastResetPassword ? fromApiDate(fragment.lastResetPassword) : null,
    lastPasswordResetBy: fragment.lastPasswordResetBy
      ? mapToUser(fragment.lastPasswordResetBy)
      : null,
    ctfId: fragment.user_national_id_2 ?? null,
    uciId: fragment.uciId ?? null,
    legalAddress: fragment.legalAddress ?? null,
    legalCity: fragment.legalCity ?? null,
    legalCountry: fragment.legalCountry ? mapToCountryCodeNullable(fragment.legalCountry) : null,
    legalPostalCode: fragment.legalPostalCode ?? null,
    legalAddressSameAsPublic: fragment.legalAddressSameAsPublic ?? false,
    education: fragment.educationalInstitution
      ? {
          institution: mapToBaseClub(fragment.educationalInstitution),
          period: educationPeriod,
        }
      : null,
  };
};

export const mapToUserListItem = (item: DashboardUserListItem): UserListItem => ({
  id: item.id,
  internalId: item.internalId,
  name: item.name ?? "",
  surname: item.surname ?? "",
  email: item.email ?? "",
  phone: item.phone ?? null,
  ...mapAvatarFields(item),
  nation: item.nation ? mapToCountryCodeNullable(item.nation) : null,
  country: item.country ? mapToCountryCodeNullable(item.country) : null,
  city: item.city ?? null,
  birthDate: item.birthDate ? fromApiCalendarDateNullable(item.birthDate) : null,
  gender: item.gender ? mapToGender(item.gender) : null,
  role: item.role ? mapToUserRole(item.role) : null,
  createdAt: new Date(item.createdAt),
  joinedAt: item.joinedAt ? new Date(item.joinedAt) : null,
  entries: item.entries,
  transactionsTotal: item.transactionsTotal,
  transactionsCurrency: item.transactionsCurrency
    ? mapToCurrencyCode(item.transactionsCurrency)
    : null,
  signupSource: item.signupSource ? mapToUserSignupSource(item.signupSource) : null,
  signupPlatform: null,
  scopes: item.scopes.map(mapToScopePair),
  sports: item.sports.map(mapToSportRef),
  clubs: (item.clubs ?? []).map(mapToClubReferenceFromRef),
  thirdPartyLicenses: [],
  tournatedTag: item.tournatedTag ?? null,
  ctfId: item.ctfId ?? null,
  connections: mapToUserConnections(item),
  capabilities: mapToCapabilitySet(item.capabilities),
});

export const mapToUserConnections = (item: DashboardUserListItem): UserConnections => ({
  fivb: item.fivb ?? null,
  duprId: item.duprId ?? null,
  ipinId: item.ipinId ?? null,
  teId: item.teId ?? null,
  atpId: item.atpId ?? null,
  wtaId: item.wtaId ?? null,
  fideId: item.fideId ?? null,
  pdlId: item.pdlId ?? null,
  uciId: item.uciId ?? null,
  ltsU10Id: item.ltsU10Id ?? null,
  teU12Id: item.teU12Id ?? null,
  wprPlayerId: item.wprPlayerId ?? null,
  itf_points: item.itf_points ?? null,
  cpcPoints: item.cpcPoints ?? null,
  maxwillPoints: item.maxwillPoints ?? null,
  fipPoints: item.fipPoints ?? null,
  otherPoints: item.otherPoints ?? null,
});

export const mapToUserWithOrganizations = (
  fragment: DetailedUserWithFederationsFragment
): UserWithOrganizations => ({
  organizations:
    fragment.federations?.map((federationFragment) => mapToOrganization(federationFragment)) ?? [],
});

const signupSourceMap: { [key in SignupSource]: UserSignupSource } = {
  [SignupSource.WEB_APP]: "web",
  [SignupSource.MOBILE_APP]: "mobile",
  [SignupSource.ADMIN]: "admin",
  [SignupSource.MANAGER]: "manager",
};

export const mapToUserSignupSource = (apiSource: SignupSource): UserSignupSource => {
  return signupSourceMap[apiSource] ?? "web";
};

const graphqlSignupSourceMap: Record<Signup_Source, UserSignupSource> = {
  [Signup_Source.Web]: "web",
  [Signup_Source.Mobile]: "mobile",
  [Signup_Source.Admin]: "admin",
  [Signup_Source.Manager]: "manager",
};

export const mapToUserSignupSourceFromGraphQL = (apiSource: Signup_Source): UserSignupSource => {
  return graphqlSignupSourceMap[apiSource] ?? "web";
};

export const mapToUserWithSports = (
  fragment: DetailedUserWithSportsFragment | UserWithSportsFragment
): UserWithSports => ({
  sports:
    fragment.user_sports?.map((sportFragment) => ({
      id: sportFragment.id,
      team: sportFragment.team ?? null,
      club: sportFragment.club ? mapToBaseClub(sportFragment.club) : null,
      sport: mapToSport(sportFragment.sport!),
    })) ?? [],
});

export const mapToUserWithPlatforms = (
  fragment: DetailedUserWithPlatformsFragment
): UserWithPlatforms => ({
  platforms:
    fragment.platforms?.map((platformFragment) => mapToBasePlatform(platformFragment)) ?? [],
  signupPlatform: fragment.signupPlatform ? mapToBasePlatform(fragment.signupPlatform) : null,
});

export const mapToUserWithMemberOfPlatforms = (
  fragment: UserWithMemberOfPlatformsFragment
): UserWithMemberOfPlatforms => ({
  memberOfPlatforms:
    fragment.member_of_plateforms?.map((memberOfPlatformFragment) => ({
      id: mapToBasePlatform(memberOfPlatformFragment.platform!)?.id,
    })) ?? [],
});

export const mapToCurrentUser = (input: DashboardCurrentUser): CurrentUser => ({
  id: input.id,
  internalId: input.internalId,
  email: input.email ?? "",
  name: input.name ?? "",
  surname: input.surname ?? "",
  ...mapAvatarFields(input),
  role: input.role ? mapToUserRole(input.role) : null,
  country: input.country ? mapToCountryCodeNullable(input.country) : null,
  memberOfPlatformIds: input.memberOfPlatformIds ?? [],
  isLeagueManager: input.isLeagueManager,
  hasFinishedTour: input.hasFinishedDashboardV3Tour,
});

export const mapToUserRole = (apiRole: string): UserRole => {
  switch (apiRole.toLowerCase()) {
    case "superadmin":
      return "superAdmin";
    case "federation":
      return "federation";
    case "clubadministrator":
      return "clubAdministrator";
    case "coach":
      return "coach";
    case "admin":
      return "admin";
    case "athlete":
      return "athlete";
    case "user":
      return "user";
    case "fan":
      return "fan";
    case "member":
      return "member";
    default:
      console.error("Unexpected user role: ", apiRole);
      return "athlete";
  }
};

/**
 * Parses additionalUserRole JSON string from API and maps to AdditionalUserRole[].
 * Handles both display format (e.g. "Club Owner", "Athlete") and camelCase (e.g. "clubOwner", "athlete").
 * Filters out empty strings and invalid values.
 */
export const mapToAdditionalUserRoles = (apiValue: string): AdditionalUserRole[] => {
  try {
    const parsed = JSON.parse(apiValue);
    if (!Array.isArray(parsed)) return [];

    const normalized = parsed
      .filter((role: unknown): role is string => typeof role === "string" && role.trim() !== "")
      .map((role) => mapToAdditionalUserRoleNullable(role.trim()))
      .filter(isDefined);

    return [...new Set(normalized)];
  } catch {
    return [];
  }
};

const mapToAdditionalUserRoleNullable = (apiRole: string): AdditionalUserRole | null => {
  const normalized = apiRole.toLowerCase().replace(/[\s_]+/g, "");
  switch (normalized) {
    case "athlete":
      return "athlete";
    case "federation member":
    case "federationmember":
      return "federationMember";
    case "club owner":
    case "clubowner":
      return "clubOwner";
    case "club manager":
    case "clubmanager":
      return "clubManager";
    case "referee":
      return "referee";
    case "tournament organizer":
    case "tournamentorganizer":
      return "tournamentOrganizer";
    case "sport fan":
    case "sportfan":
    case "fan":
      return "sportFan";
    case "other":
      return "other";
    default:
      return null;
  }
};

export const mapToApiAdditionalUserRoles = (roles: AdditionalUserRole[]): string => {
  return JSON.stringify([...new Set(roles)]);
};

export const mapToUserPlatformRole = (apiRole: string): UserPlatformRole => {
  switch (apiRole) {
    case "platformManager":
      return "platformManager";
    case "platformOwner":
      return "platformOwner";
    case "leagueManager":
      return "leagueManager";
    case "suborganization":
      return "subOrganization";
    case "federation":
      return "federation";
    case "superAdmin":
      return "superAdmin";
    default:
      console.error("Unexpected user role: ", apiRole);
      return "leagueManager";
  }
};

export const mapToApiUserRole = (role: UserRole): string => {
  switch (role) {
    case "superAdmin":
      return "superAdmin";
    case "federation":
      return "federation";
    case "clubAdministrator":
      return "clubAdministrator";
    case "coach":
      return "coach";
    case "admin":
      return "admin";
    case "athlete":
      return "athlete";
    case "user":
      return "user";
    case "fan":
      return "fan";
    case "member":
      return "member";
  }
};

const genderMapReverse: { [key in Gender]: ApiGender } = {
  male: ApiGender.Male,
  female: ApiGender.Female,
};

export const mapToApiGender = (gender: Gender): ApiGender => {
  return genderMapReverse[gender];
};

export const mapToDashboardUserGender = (
  gender: Gender
): DashboardCreateUserGender & DashboardUpdateUserGender => {
  return gender === "male" ? DashboardCreateUserGender.Male : DashboardCreateUserGender.Female;
};

const dashboardAdditionalRoleMap: {
  [key in AdditionalUserRole]: DashboardCreateUserAdditionalRolesItem &
    DashboardUpdateUserAdditionalRolesItem;
} = {
  athlete: DashboardCreateUserAdditionalRolesItem.ATHLETE,
  clubManager: DashboardCreateUserAdditionalRolesItem.CLUB_MANAGER,
  clubOwner: DashboardCreateUserAdditionalRolesItem.CLUB_OWNER,
  federationMember: DashboardCreateUserAdditionalRolesItem.FEDERATION_MEMBER,
  tournamentOrganizer: DashboardCreateUserAdditionalRolesItem.TOURNAMENT_ORGANIZER,
  referee: DashboardCreateUserAdditionalRolesItem.REFEREE,
  sportFan: DashboardCreateUserAdditionalRolesItem.FAN,
  other: DashboardCreateUserAdditionalRolesItem.OTHER,
};

export const mapToDashboardUserAdditionalRoles = (
  roles: AdditionalUserRole[]
): (DashboardCreateUserAdditionalRolesItem & DashboardUpdateUserAdditionalRolesItem)[] => {
  return [...new Set(roles)].map((role) => dashboardAdditionalRoleMap[role]);
};

export const mapToGender = (gender: string): Gender => {
  switch (gender?.toLowerCase()) {
    case "male":
      return "male";
    case "female":
      return "female";
    default:
      return "male";
  }
};

export const thirdPartyLicenseTypeMapReverse: {
  [key in ThirdPartyLicenseType]: ThirdPartyUserProfileConnectionType;
} = {
  dupr: ThirdPartyUserProfileConnectionType.Dupr,
  wpr: ThirdPartyUserProfileConnectionType.Wpr,
  ipin: ThirdPartyUserProfileConnectionType.Ipin,
  itfPoints: ThirdPartyUserProfileConnectionType.ItfPoints,
  fideId: ThirdPartyUserProfileConnectionType.Fide,
  fipPoints: ThirdPartyUserProfileConnectionType.FipPoints,
  fivb: ThirdPartyUserProfileConnectionType.Fivb,
};

export const mapToApiThirdPartyLicenseType = (
  type: ThirdPartyLicenseType
): ThirdPartyUserProfileConnectionType => {
  return thirdPartyLicenseTypeMapReverse[type];
};

/**
 * Structural source for {@link mapToUserReference}: the fields it reads, with
 * `email` optional so the full `DashboardUserRef` and the leaner emailless refs
 * (`SearchUserRef` from global search, `DashboardLeagueOrganizerRef` from the
 * league-organizer picker) all satisfy it. An emailless ref maps to
 * `email: ""`, so a consumer of those must not render `email` — show
 * `tournatedTag` instead.
 */
type UserReferenceSource = {
  id: string;
  name: string | null;
  surname: string | null;
  email?: string | null;
  tournatedTag?: string | null;
  avatarFile?: { url?: string | null; fileId?: string | null } | null;
  legacyAvatarUrl?: string | null;
};

export const mapToUserReference = (input: UserReferenceSource): UserReference => ({
  id: input.id,
  name: input.name ?? "",
  surname: input.surname ?? "",
  email: input.email ?? "",
  tournatedTag: input.tournatedTag ?? null,
  ...mapAvatarFields(input),
});

// REST `DashboardUserDetail` (`GET /dashboard/v1/users/:id`, and the email-lookup
// result) → the clean `UserDetail` domain type. Only the fields the REST endpoint
// actually returns are mapped (no hardcoded-null third-party IDs / audit fields).
// Replace (rather than intersect) the generated field so this remains compatible
// once API codegen exposes `state` / `representingState` with their own
// generated enum types.
type DashboardUserDetailWithState = Omit<DashboardUserDetail, "state" | "representingState"> & {
  state?: string | null;
  representingState?: string | null;
};

export const mapToUserDetail = (input: DashboardUserDetailWithState): UserDetail => ({
  id: input.id,
  internalId: input.internalId,
  tournatedTag: input.tournatedTag,
  name: input.name ?? "",
  middleName: input.middleName ?? null,
  surname: input.surname ?? "",
  email: input.email ?? "",
  contactEmail: input.contactEmail ?? null,
  ...mapAvatarFields(input),
  phone: input.phone ?? null,
  contactPhone: input.contactPhone ?? null,
  gender: input.gender ? mapToGender(input.gender) : null,
  birthDate: input.birthDate ? fromApiCalendarDateNullable(input.birthDate) : null,
  nation: input.nation ? mapToCountryCodeNullable(input.nation) : null,
  representingState:
    input.representingState && isUSState(input.representingState) ? input.representingState : null,
  state: input.state && isUSState(input.state) ? input.state : null,
  personalId: input.personalId ?? null,
  additionalRoles: (input.additionalRoles ?? [])
    .map((role) => mapToAdditionalUserRoleNullable(role))
    .filter(isDefined),
  country: input.country ? mapToCountryCodeNullable(input.country) : null,
  city: input.city ?? null,
  address: input.address ?? null,
  postalCode: input.postalCode ?? null,
  legalAddressSameAsPublic: !input.legalAddressDifferent,
  legalAddress: input.legalAddress ?? null,
  legalCity: input.legalCity ?? null,
  legalCountry: input.legalCountry ? mapToCountryCodeNullable(input.legalCountry) : null,
  legalPostalCode: input.legalPostalCode ?? null,
  education: input.education.institution
    ? {
        institution: {
          id: input.education.institution.id,
          clubName: input.education.institution.title ?? "",
          name: input.education.institution.title ?? "",
          logoFile: null,
          legacyLogoUrl: null,
          city: null,
          type: null,
        },
        period:
          input.education.startDate && input.education.endDate
            ? {
                start: fromApiCalendarDateNullable(input.education.startDate)!,
                end: fromApiCalendarDateNullable(input.education.endDate)!,
              }
            : null,
      }
    : null,
});
